import type { EfficiencyTier } from './efficiency'
import type { MemoryType, MonitorSize, Platform, StorageType } from './parts'

/**
 * What the visitor describes: the hardware, and how the machine is used. The
 * hardware alone sizes the power supply; usage only feeds the energy estimate.
 */

/** `count` parts of one kind. */
export interface PartLine<T extends string> {
  readonly type: T
  readonly count: number
}

/**
 * A processor or graphics card: a catalogue id, `custom` for a typed wattage,
 * or (graphics only) `none` for integrated graphics.
 */
export interface ModelChoice {
  readonly model: string
  /** Used only when `model` is `custom`; kept otherwise so switching back restores it. */
  readonly customWatts: number
  readonly count: number
}

export const CUSTOM_MODEL = 'custom'
export const NO_GPU = 'none'

export interface Build {
  readonly platform: Platform
  readonly cpu: ModelChoice
  readonly gpu: ModelChoice
  readonly memory: PartLine<MemoryType>
  /** Two rows, as most builds mix a fast drive with a big one. */
  readonly storage: readonly [PartLine<StorageType>, PartLine<StorageType>]
  /**
   * Watts for anything the form does not list — an optical drive, a sound card,
   * lighting — typed by the visitor. Zero by default.
   */
  readonly otherWatts: number
  readonly fans: number
}

export interface Usage {
  /** Hours a day the computer is on. */
  readonly hoursPerDay: number
  /** Of those, hours spent gaming, rendering or otherwise under full load. */
  readonly heavyHoursPerDay: number
  readonly monitors: PartLine<MonitorSize>
  readonly efficiency: EfficiencyTier
  /** In whatever currency the visitor pays in; results come back in the same one. */
  readonly pricePerKwh: number
}

export interface Spec {
  readonly build: Build
  readonly usage: Usage
}

/**
 * Inclusive upper bounds for every count. The pickers offer exactly these, and a
 * share link outside them is rejected rather than trusted.
 */
export const LIMITS = {
  cpuCount: 8,
  gpuCount: 4,
  memory: 16,
  storage: 12,
  fans: 16,
  monitors: 6,
  /** At least one: a machine that is never on has no energy to estimate. */
  minHoursPerDay: 1,
  hoursPerDay: 24,
  /** Above any single part's real draw, so a typo cannot dwarf the build. */
  customWatts: 2000,
  /** Generous for a handful of extras, and still catches a stray extra zero. */
  otherWatts: 1000,
} as const

export const DEFAULT_SPEC: Spec = {
  build: {
    platform: 'desktop',
    cpu: { model: 'ryzen-7-9800x3d', customWatts: 125, count: 1 },
    gpu: { model: 'rtx-5070', customWatts: 250, count: 1 },
    memory: { type: 'ddr5', count: 2 },
    storage: [
      { type: 'nvme', count: 1 },
      { type: 'hdd-35', count: 0 },
    ],
    otherWatts: 0,
    fans: 3,
  },
  usage: {
    hoursPerDay: 8,
    heavyHoursPerDay: 2,
    monitors: { type: 'medium', count: 1 },
    efficiency: 'gold',
    pricePerKwh: 0.2,
  },
}

export function isCount(value: number, max: number, min = 0): boolean {
  return Number.isInteger(value) && value >= min && value <= max
}

export function isValidCustomWatts(watts: number): boolean {
  return Number.isFinite(watts) && watts > 0 && watts <= LIMITS.customWatts
}

export function isValidOtherWatts(watts: number): boolean {
  return Number.isFinite(watts) && watts >= 0 && watts <= LIMITS.otherWatts
}

export function isValidPrice(price: number): boolean {
  return Number.isFinite(price) && price >= 0
}
